import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { StaggeredCategoryTick } from "@/components/charts/StaggeredCategoryTick";
import { ViewScopeTabs, viewToParams } from "@/components/admin/ViewScopeTabs";

const COLORS = ["#059669", "#EAB308", "#F97316", "#EC4899", "#6366F1", "#0EA5E9", "#10B981", "#8B5CF6", "#EF4444"];

const Stat = ({ label, value }) => (
  <div className="tsw-stat" data-testid={`stat-${label}`}>
    <div className="text-xs font-bold uppercase tracking-widest text-emerald-600">{label}</div>
    <div className="font-display font-bold text-3xl text-slate-900">{value ?? "—"}</div>
  </div>
);

const AdminDashboard = () => {
  const { lang, t, pickI18n, tProfile: tProfileGlobal, tCountry: tCountryGlobal, tQuestionnaireTitle } = useLang();
  const [stats, setStats] = useState(null);
  const [countryMap, setCountryMap] = useState({});
  const [profilesMap, setProfilesMap] = useState({});
  // Phase A17-B — 3-state view scope for stats. Default "production".
  const [viewScope, setViewScope] = useState("production");

  useEffect(() => {
    const params = viewToParams(viewScope);
    api.get("/admin/stats", { params }).then((r) => setStats(r.data));
  }, [viewScope]);
  // Load the trilingual country dictionary once so the chart's X-axis can
  // render "España / 西班牙 / Spain" instead of the raw ISO code coming
  // from /admin/stats. The endpoint is already used by Register / Admin
  // Users, so the response is typically warm in the browser cache.
  useEffect(() => {
    api.get("/countries").then((r) => {
      const map = {};
      for (const c of r.data || []) map[c.code] = c;
      setCountryMap(map);
    }).catch(() => {});
    api.get("/profiles").then((r) => {
      const map = {};
      for (const p of r.data || []) map[p.key] = p;
      setProfilesMap(map);
    }).catch(() => {});
  }, []);

  if (!stats) return <div>{t("common.loading")}</div>;

  // Translate the stable backend keys into language-appropriate display
  // labels for the chart axes/legends. Internal payload keys
  // (`profile_key`, `lead_status`, `questionnaire_id`) are NEVER mutated
  // — the analytics dataset stays normalized for downstream tooling.
  const tProfile = (key) => tProfileGlobal(key, profilesMap[key]);
  const tStatus = (key) => {
    const r = t(`leadStatuses.${key}`);
    return r && r !== `leadStatuses.${key}` ? r : key;
  };
  const tCountry = (code) => tCountryGlobal(code, countryMap[code]);
  // Pick the questionnaire title in the active language. The backend
  // returns `title_en` / `title_es` / `title_zh` alongside the legacy
  // `title` field. If ALL titles are empty (e.g. orphan response whose
  // questionnaire was deleted, or a mid-edit row with no en title),
  // fall back to a localized "Archived/Unnamed questionnaire" copy
  // instead of leaking the internal id to the chart axis.
  const tQuestionnaire = (q) => tQuestionnaireTitle(q);

  const profileChartData = stats.by_profile.map((x) => ({ name: tProfile(x.key), key: x.key, count: x.count }));
  const countryChartData = stats.by_country.map((x) => ({ name: tCountry(x.country), code: x.country, count: x.count }));
  const statusChartData = stats.by_status.map((x) => ({ name: tStatus(x.status), key: x.status, count: x.count }));
  const questionnaireChartData = stats.by_questionnaire.map((q) => ({
    ...q,
    title: tQuestionnaire(q),
  }));

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tsw-overline">{t("admin.title")}</div>
          <h1 className="font-display font-bold text-3xl text-slate-900">{t("admin.stats")}</h1>
        </div>
        <ViewScopeTabs value={viewScope} onChange={setViewScope} testIdPrefix="dashboard-view-scope" />
      </div>

      {viewScope === "production" && (stats?.test_response_count ?? 0) > 0 && (
        <div
          className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600"
          data-testid="dashboard-test-hidden-banner"
        >
          {t("phase2.statsHiddenBanner").replace("{n}", stats.test_response_count)}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Stat label={t("admin.totalUsers")} value={stats.total_users} />
        <Stat label={t("admin.verifiedUsers")} value={stats.verified_users} />
        <Stat label={t("admin.pilotReady")} value={stats.pilot_ready} />
        <Stat label={t("admin.consentMarketing")} value={stats.consent_marketing} />
        <Stat label={t("admin.totalResponses")} value={stats.total_responses} />
        <Stat label={t("admin.activeQuestionnaires")} value={stats.total_questionnaires} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="tsw-card p-5">
          <div className="font-semibold text-slate-900 mb-3">{t("admin.byProfile")}</div>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={profileChartData} margin={{ top: 8, right: 12, bottom: 96, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" interval={0} height={110} tick={<StaggeredCategoryTick lang={lang} />} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#059669" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="tsw-card p-5">
          <div className="font-semibold text-slate-900 mb-3">{t("admin.byCountry")}</div>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={countryChartData} margin={{ top: 8, right: 12, bottom: 96, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" interval={0} height={110} tick={<StaggeredCategoryTick lang={lang} />} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#F97316" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="tsw-card p-5">
          <div className="font-semibold text-slate-900 mb-3">{t("admin.byStatus")}</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie dataKey="count" data={statusChartData} cx="50%" cy="50%" outerRadius={90} label>
                {statusChartData.map((x, i) => <Cell key={x.key || `status-${i}`} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="tsw-card p-5">
          <div className="font-semibold text-slate-900 mb-3">{t("admin.byQuestionnaire")}</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart layout="vertical" data={questionnaireChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis dataKey="title" type="category" width={150} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#EC4899" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
